/* 리뷰에서 화면이 필요한 값을 뽑는 순수 함수들.
 *
 * 불변식: stores.json 은 읽기만 한다. 리뷰는 SQL Connect 저장소에만 쓴다.
 * 주간 배치가 매장 목록을 통째로 갈아엎어도 기록은 store_id 로 다시 붙는다.
 *
 * visits 는 **팀 전원의 방문**이다. 한 건에 author(누가) 와 mine(내 것인가)이 붙어
 * 온다(connector/queries.gql). want_to_go 만 내 값이다 — 여기 있는 집계 함수가
 * 전부 '우리 팀 기준'인 이유고, 지우기는 mine 인 것에만 열어주는 이유다.
 *
 * 저장은 여기 없다. 쓰기는 lib/api.js 가 하고, 쓴 뒤 그 매장만 다시 읽어 상태를
 * 갈아끼운다 — '방문도 찜도 없으면 기록이 없는 것'이라는 판정과 방문 정렬을
 * 조립 질의 한 곳에서만 정하기 위해서다. 두 곳에서 정하면 반드시 어긋난다.
 *
 * 여기 있는 함수는 전부 순수 함수다. 리뷰 객체를 받아 값을 돌려줄 뿐이다.
 */

/* localStorage 시절의 키. 이제 읽는 곳은 최초 1회 서버로 올려보내는 마이그레이션뿐이다.
   올린 뒤에도 지우지 않고 ':migrated' 를 붙여 남겨둔다 — 되돌릴 수 있어야 한다. */
export const LEGACY_KEY = "lunch-journal:reviews:v1";

const EMPTY = { want_to_go: false, visits: [] };

export const reviewOf = (reviews, id) => reviews[id] || EMPTY;

/** 방문 한 건의 별점(1~5).
    별점 이전 기록(판정 again/ok/never)의 환산은 가져오기 때 서버가 끝낸다 —
    DB 의 rating 은 not null 이라 여기까지 오는 방문에는 늘 별점이 있다. */
export const ratingOfVisit = (v) => v.rating;

/** 방문 한 건을 남긴 사람. 이름도 이메일도 없는 계정이 있어서 마지막 칸이 필요하다 —
    이름을 못 찾았다고 리뷰를 감추면, 그 사람 기록만 조용히 없는 것이 된다. */
export const authorOf = (v) => v.author || "익명";

/* 마커 색은 별점을 세 단계로 접어서 쓴다. 지도 위에서 다섯 색을 구분하는 건 무리고,
   핀을 보고 알고 싶은 건 '또 갈 만한가' 하나다. */
const GOOD = 4;   // 4점 이상 — 또 갈 만하다
const BAD = 2;    // 2점 이하 — 아니다

/** 우리 기록만으로 매긴 별점 — 팀 전원의 방문 평균이다. 바깥 별점(naver_review_count
    등)은 섞지 않는다. 기록이 없으면 0.0점 · 0건 — 아직 아무도 안 가봤다는 뜻이다. */
export function ratingOf(reviews, id) {
  const { visits } = reviewOf(reviews, id);
  if (!visits.length) return { score: 0, count: 0 };
  const sum = visits.reduce((acc, v) => acc + ratingOfVisit(v), 0);
  return { score: sum / visits.length, count: visits.length };
}

/* 마커 색은 **평균**으로 정한다. 혼자 쓰던 시절에는 가장 최근 방문 한 건을
   봤다 — 내 기록이니 최근 것이 곧 최신 판정이었다. 팀 기록에서는 그게 '마지막에
   기록한 사람 한 명의 점수'가 되어, 다섯 명이 4점을 준 집이 오늘 1점 하나로 빨갛게
   변한다. 옆에 적히는 별점(ratingOf)도 평균이라, 색과 숫자가 어긋나기까지 한다. */
export function statusOf(reviews, store) {
  const r = reviewOf(reviews, store.id);
  if (r.visits.length) {
    const { score } = ratingOf(reviews, store.id);
    return score >= GOOD ? "again" : score <= BAD ? "never" : "ok";
  }
  return r.want_to_go ? "want" : "unvisited";
}
